import LanguageTwoToneIcon from '@mui/icons-material/LanguageTwoTone';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import i18n from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchSupportedLanguages,
  getSavedLanguage,
  saveLanguage,
} from '../i18n/languageService';

type Lang = { code: string; name: string };

export default function LanguageSelector({ big }: { big?: boolean }) {
  const { t } = useTranslation('menu');
  const [langs, setLangs] = useState<Lang[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const timeoutRef = useRef<null | NodeJS.Timeout>(null);
  const [currentLang, setCurrentLang] = useState<string>(
    i18n.language || getSavedLanguage() || 'fr-FR'
  );

  const open = Boolean(anchorEl);

  useEffect(() => {
    let mounted = true;
    fetchSupportedLanguages()
      .then((list) => mounted && setLangs(list))
      .catch(() => {});

    const handleLanguageChanged = (code: string) => {
      setCurrentLang(code || 'fr-FR');
    };

    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      mounted = false;
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    timeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, 200);
  };

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
    saveLanguage(code);
    setCurrentLang(code);
    handleClose();
  };

  const languages = langs.length
    ? langs
    : [{ code: 'fr-FR', name: 'Français' }];

  const safeCurrentLang = currentLang || i18n.language || 'fr-FR';

  const activeLang =
    languages.find(
      (l) =>
        l.code === safeCurrentLang ||
        safeCurrentLang.startsWith(l.code.split('-')[0])
    ) ?? languages[0];

  const handleMenuEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  return (
    <Box sx={{ display: 'inline' }}>
      <Button
        className="nav-item-static"
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : undefined}
        startIcon={<LanguageTwoToneIcon />}
      >
        {t('Langue')}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        // allows the mouse to "see through" the menu container
        sx={{ pointerEvents: 'none' }}
        slotProps={{
          paper: {
            onMouseEnter: handleMenuEnter,
            onMouseLeave: handleClose,
            sx: {
              // reactivate pointer events for the menu itself
              pointerEvents: 'auto',
              marginTop: '-5px',
            },
          },
          backdrop: {
            sx: { backgroundColor: 'transparent', pointerEvents: 'none' },
          },
        }}
        disableAutoFocusItem
        disableEnforceFocus
      >
        {languages.map((l) => (
          <MenuItem
            key={l.code}
            selected={l.code === activeLang?.code}
            onClick={() => {
              handleLanguageChange(l.code);
              setAnchorEl(null);
            }}
          >
            {l.name}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
